import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Loader2, Plus, Edit, Trash2, ClipboardList, Inbox, AlertTriangle,
  Repeat, Calendar as CalIcon, Bell, User, Briefcase, Flag, CheckCircle2, Circle, Sparkles, FileText,
} from "lucide-react";
import { toast } from "sonner";
import ContractsTab from "@/components/admin/ContractsTab";
import OverrunSettingsTab from "@/components/admin/OverrunSettingsTab";
import AIActivityTab from "@/components/admin/AIActivityTab";
import DisputesTab from "@/components/admin/DisputesTab";

// ---- Helpers ----

const RECURRENCE_LABEL = {
  monthly: "Mensuelle",
  yearly: "Annuelle",
  weekly: "Hebdomadaire",
  one_time: "Ponctuelle",
};

const PRIORITY_CONFIG = {
  high: { label: "Haute", className: "bg-red-100 text-red-800" },
  medium: { label: "Moyenne", className: "bg-amber-100 text-amber-900" },
  low: { label: "Basse", className: "bg-slate-100 text-slate-800" },
};

const WEEKDAYS = ["Lundi", "Mardi", "Mercredi", "Jeudi", "Vendredi", "Samedi", "Dimanche"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" }) : "—");

// ---- Recurring Task Dialog ----

const RecurringTaskDialog = ({ open, onClose, task, employees, subcontractors, onSaved }) => {
  const editing = !!task;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(task ? {
        title: task.title || "",
        description: task.description || "",
        recurrence_type: task.recurrence_type || "monthly",
        day_of_month: task.day_of_month ?? "",
        month: task.month ?? "",
        day_of_week: task.day_of_week ?? "",
        due_date: task.due_date || "",
        require_check: task.require_check !== false,
        reminder_enabled: !!task.reminder_enabled,
        reminder_days_before: task.reminder_days_before ?? 3,
        reminder_email: task.reminder_email || "",
        assignee_employee_id: task.assignee_employee_id || "none",
        assignee_subcontractor_id: task.assignee_subcontractor_id || "none",
      } : {
        title: "", description: "", recurrence_type: "monthly",
        day_of_month: 5, month: "", day_of_week: "", due_date: "",
        require_check: true, reminder_enabled: false, reminder_days_before: 3,
        reminder_email: "",
        assignee_employee_id: "none", assignee_subcontractor_id: "none",
      });
    }
  }, [open, task]);

  const handleSubmit = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    if (form.recurrence_type === "one_time" && !form.due_date) { toast.error("Date requise pour une tâche ponctuelle"); return; }
    if (form.recurrence_type === "monthly" && !form.day_of_month) { toast.error("Jour du mois requis"); return; }
    if (form.recurrence_type === "yearly" && (!form.day_of_month || !form.month)) { toast.error("Mois + jour requis"); return; }
    if (form.recurrence_type === "weekly" && form.day_of_week === "") { toast.error("Jour de la semaine requis"); return; }

    try {
      setSaving(true);
      const payload = {
        title: form.title,
        description: form.description || null,
        recurrence_type: form.recurrence_type,
        day_of_month: form.day_of_month === "" ? null : Number(form.day_of_month),
        month: form.month === "" ? null : Number(form.month),
        day_of_week: form.day_of_week === "" ? null : Number(form.day_of_week),
        due_date: form.due_date || null,
        require_check: form.require_check,
        reminder_enabled: form.reminder_enabled,
        reminder_days_before: Number(form.reminder_days_before) || 0,
        reminder_email: form.reminder_email || null,
        assignee_employee_id: form.assignee_employee_id === "none" ? null : form.assignee_employee_id,
        assignee_subcontractor_id: form.assignee_subcontractor_id === "none" ? null : form.assignee_subcontractor_id,
      };
      if (editing) await api.updateRecurringTask(task.id, payload);
      else await api.createRecurringTask(payload);
      toast.success(editing ? "Tâche modifiée" : "Tâche créée");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier la tâche récurrente" : "Nouvelle tâche récurrente"}</DialogTitle>
          <DialogDescription>
            Configurez la récurrence, les rappels et l'assignation. La case à cocher se remet à zéro à chaque échéance.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Titre *</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex : Déclaration TVA" data-testid="task-title" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div>
            <Label>Récurrence *</Label>
            <Select value={form.recurrence_type || "monthly"} onValueChange={(v) => setForm({ ...form, recurrence_type: v })}>
              <SelectTrigger data-testid="task-recurrence"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="monthly">Mensuelle (tous les X du mois)</SelectItem>
                <SelectItem value="yearly">Annuelle (chaque année)</SelectItem>
                <SelectItem value="weekly">Hebdomadaire (tous les X)</SelectItem>
                <SelectItem value="one_time">Ponctuelle (une seule fois)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {form.recurrence_type === "monthly" && (
            <div>
              <Label>Jour du mois *</Label>
              <Input type="number" min="1" max="31" value={form.day_of_month || ""} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} placeholder="Ex : 5" data-testid="task-dayofmonth" />
              <p className="text-xs text-muted-foreground mt-1">31 sera ramené au dernier jour du mois court (ex : 28/29 en février).</p>
            </div>
          )}
          {form.recurrence_type === "yearly" && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label>Mois *</Label>
                <Select value={String(form.month || "")} onValueChange={(v) => setForm({ ...form, month: v })}>
                  <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, idx) => <SelectItem key={idx} value={String(idx + 1)}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Jour *</Label>
                <Input type="number" min="1" max="31" value={form.day_of_month || ""} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} />
              </div>
            </div>
          )}
          {form.recurrence_type === "weekly" && (
            <div>
              <Label>Jour de la semaine *</Label>
              <Select value={String(form.day_of_week || "")} onValueChange={(v) => setForm({ ...form, day_of_week: v })}>
                <SelectTrigger><SelectValue placeholder="Sélectionner" /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, idx) => <SelectItem key={idx} value={String(idx)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {form.recurrence_type === "one_time" && (
            <div>
              <Label>Date *</Label>
              <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} />
            </div>
          )}

          <div className="border rounded p-3 bg-slate-50 space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="require_check"
                checked={form.require_check ?? true}
                onCheckedChange={(v) => setForm({ ...form, require_check: !!v })}
                data-testid="task-require-check"
              />
              <Label htmlFor="require_check" className="cursor-pointer">Cocher manuellement à chaque échéance</Label>
            </div>
            <p className="text-xs text-muted-foreground">Si décoché : la tâche est listée pour rappel uniquement, sans case à cocher.</p>
          </div>

          <div className="border rounded p-3 bg-amber-50 space-y-2">
            <div className="flex items-center gap-2">
              <Checkbox
                id="reminder_enabled"
                checked={form.reminder_enabled || false}
                onCheckedChange={(v) => setForm({ ...form, reminder_enabled: !!v })}
                data-testid="task-reminder-enabled"
              />
              <Label htmlFor="reminder_enabled" className="cursor-pointer font-medium">Recevoir un rappel par email</Label>
            </div>
            {form.reminder_enabled && (
              <div className="grid grid-cols-2 gap-2 pl-6">
                <div>
                  <Label className="text-xs">Jours avant échéance</Label>
                  <Input type="number" min="0" max="30" value={form.reminder_days_before ?? 3} onChange={(e) => setForm({ ...form, reminder_days_before: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs">Email (facultatif)</Label>
                  <Input type="email" value={form.reminder_email || ""} onChange={(e) => setForm({ ...form, reminder_email: e.target.value })} placeholder="Votre email par défaut" />
                </div>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Assigner à un salarié</Label>
              <Select value={form.assignee_employee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_employee_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ou à un sous-traitant</Label>
              <Select value={form.assignee_subcontractor_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_subcontractor_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {subcontractors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-task-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Recurring Tasks Tab ----

const RecurringTab = ({ employees, subcontractors }) => {
  const [tasks, setTasks] = useState([]);
  const [occurrences, setOccurrences] = useState({ items: [], overdue_count: 0, upcoming_count: 0 });
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState({ open: false, task: null });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [tRes, oRes] = await Promise.all([
        api.getRecurringTasks(),
        api.getTaskOccurrences(),
      ]);
      setTasks(tRes.data);
      setOccurrences(oRes.data);
    } catch (_e) {
      toast.error("Erreur chargement tâches récurrentes");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (occ, checked) => {
    try {
      await api.toggleOccurrence({ task_id: occ.task_id, due_date: occ.due_date, completed: checked });
      fetchAll();
    } catch (_e) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette tâche récurrente et toutes ses occurrences ?")) return;
    try {
      await api.deleteRecurringTask(id);
      toast.success("Tâche supprimée");
      fetchAll();
    } catch (_e) { toast.error("Erreur de suppression"); }
  };

  const handleSeedFrTemplates = async () => {
    if (!window.confirm("Ajouter les rappels fiscaux et sociaux français standards ?\n\nTVA, DSN, URSSAF, Liasse, CFE, CVAE, 4 acomptes IS — 10 tâches au total. Les doublons sont ignorés.")) return;
    try {
      const { data } = await api.seedFrFiscalTemplates();
      if (data.created_count > 0) {
        toast.success(`${data.created_count} rappel(s) ajouté(s)${data.skipped_count ? ` (${data.skipped_count} déjà existants)` : ""}`);
      } else {
        toast.info("Tous les rappels fiscaux standards sont déjà présents");
      }
      fetchAll();
    } catch (_e) { toast.error("Erreur lors de l'ajout des rappels standards"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge variant={occurrences.overdue_count > 0 ? "destructive" : "outline"} className="text-base px-3 py-1" data-testid="overdue-badge">
            {occurrences.overdue_count > 0 && <AlertTriangle className="h-4 w-4 mr-1 inline" />}
            {occurrences.overdue_count} en retard
          </Badge>
          <Badge className="bg-sky-100 text-sky-900 text-base px-3 py-1">{occurrences.upcoming_count} à venir</Badge>
          <span className="text-sm text-muted-foreground">{tasks.length} tâche(s) configurée(s)</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={handleSeedFrTemplates} data-testid="seed-fr-templates-btn">
            <Sparkles className="h-4 w-4 mr-1 text-amber-500" /> Rappels fiscaux standards
          </Button>
          <Button onClick={() => setDialog({ open: true, task: null })} data-testid="add-recurring-btn">
            <Plus className="h-4 w-4 mr-1" /> Nouvelle tâche récurrente
          </Button>
        </div>
      </div>

      {/* Occurrences list */}
      <Card>
        <CardContent className="p-0">
          {occurrences.items.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">Aucune tâche récurrente programmée. Créez-en une pour commencer.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Tâche</TableHead>
                  <TableHead>Échéance</TableHead>
                  <TableHead>Récurrence</TableHead>
                  <TableHead>Rappel</TableHead>
                  <TableHead>Assigné à</TableHead>
                  <TableHead>État</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {occurrences.items.map((occ) => (
                  <TableRow
                    key={`${occ.task_id}-${occ.due_date}`}
                    className={occ.is_overdue ? "bg-red-50/60" : occ.completed ? "opacity-60" : ""}
                    data-testid={`occurrence-${occ.task_id}-${occ.due_date}`}
                  >
                    <TableCell>
                      {occ.require_check ? (
                        <Checkbox
                          checked={occ.completed}
                          onCheckedChange={(v) => handleToggle(occ, !!v)}
                          data-testid={`toggle-${occ.task_id}-${occ.due_date}`}
                        />
                      ) : (
                        <Circle className="h-4 w-4 text-muted-foreground" />
                      )}
                    </TableCell>
                    <TableCell>
                      <div className={`font-medium ${occ.completed ? "line-through" : ""}`}>{occ.title}</div>
                      {occ.description && <div className="text-xs text-muted-foreground">{occ.description}</div>}
                    </TableCell>
                    <TableCell className="text-sm">{fmtDate(occ.due_date)}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        <Repeat className="h-3 w-3 mr-1" />
                        {RECURRENCE_LABEL[occ.recurrence_type] || occ.recurrence_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {occ.reminder_enabled ? <Badge className="bg-amber-100 text-amber-900 text-xs"><Bell className="h-3 w-3 mr-1" />Actif</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {occ.assignee_employee_name && <Badge variant="outline" className="text-xs"><User className="h-3 w-3 mr-1" />{occ.assignee_employee_name}</Badge>}
                      {occ.assignee_subcontractor_name && <Badge variant="outline" className="text-xs"><Briefcase className="h-3 w-3 mr-1" />{occ.assignee_subcontractor_name}</Badge>}
                      {!occ.assignee_employee_name && !occ.assignee_subcontractor_name && <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      {occ.completed ? <Badge className="bg-green-100 text-green-800"><CheckCircle2 className="h-3 w-3 mr-1" />Fait</Badge>
                        : occ.is_overdue ? <Badge className="bg-red-100 text-red-800"><AlertTriangle className="h-3 w-3 mr-1" />En retard</Badge>
                        : <Badge className="bg-sky-100 text-sky-900">À faire</Badge>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Templates list */}
      {tasks.length > 0 && (
        <Card>
          <CardContent className="pt-4">
            <h3 className="text-sm font-semibold mb-2">Tâches configurées</h3>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titre</TableHead>
                  <TableHead>Récurrence</TableHead>
                  <TableHead>Rappel</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((t) => (
                  <TableRow key={t.id} data-testid={`recurring-row-${t.id}`}>
                    <TableCell className="font-medium">{t.title}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-xs">
                        {RECURRENCE_LABEL[t.recurrence_type]}
                        {t.recurrence_type === "monthly" && ` — ${t.day_of_month} de chaque mois`}
                        {t.recurrence_type === "yearly" && ` — ${t.day_of_month} ${MONTHS[(t.month || 1) - 1]}`}
                        {t.recurrence_type === "weekly" && ` — ${WEEKDAYS[t.day_of_week] || ""}`}
                        {t.recurrence_type === "one_time" && ` — ${fmtDate(t.due_date)}`}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {t.reminder_enabled ? <Badge className="bg-amber-100 text-amber-900 text-xs">{t.reminder_days_before}j avant</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, task: t })} data-testid={`edit-recurring-${t.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} data-testid={`delete-recurring-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <RecurringTaskDialog
        open={dialog.open}
        task={dialog.task}
        employees={employees}
        subcontractors={subcontractors}
        onClose={() => setDialog({ open: false, task: null })}
        onSaved={() => { setDialog({ open: false, task: null }); fetchAll(); }}
      />
    </div>
  );
};

// ---- Backlog Task Dialog ----

const BacklogTaskDialog = ({ open, onClose, task, employees, subcontractors, onSaved }) => {
  const editing = !!task;
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(task ? {
        title: task.title || "",
        description: task.description || "",
        category: task.category || "",
        due_date: task.due_date || "",
        priority: task.priority || "medium",
        assignee_employee_id: task.assignee_employee_id || "none",
        assignee_subcontractor_id: task.assignee_subcontractor_id || "none",
      } : {
        title: "", description: "", category: "", due_date: "",
        priority: "medium", assignee_employee_id: "none", assignee_subcontractor_id: "none",
      });
    }
  }, [open, task]);

  const handleSubmit = async () => {
    if (!form.title) { toast.error("Titre requis"); return; }
    try {
      setSaving(true);
      const payload = {
        title: form.title,
        description: form.description || null,
        category: form.category || null,
        due_date: form.due_date || null,
        priority: form.priority,
        assignee_employee_id: form.assignee_employee_id === "none" ? null : form.assignee_employee_id,
        assignee_subcontractor_id: form.assignee_subcontractor_id === "none" ? null : form.assignee_subcontractor_id,
      };
      if (editing) await api.updateBacklogTask(task.id, payload);
      else await api.createBacklogTask(payload);
      toast.success(editing ? "Tâche modifiée" : "Tâche créée");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Modifier la tâche" : "Nouvelle tâche (à faire quand on a le temps)"}</DialogTitle>
          <DialogDescription>
            Ajoutez une tâche non urgente avec priorité, date cible et assignation optionnelles.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Titre *</Label>
            <Input value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="backlog-title" placeholder="Ex : Repeindre le local, Formation SST Jean..." />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Catégorie</Label>
              <Input value={form.category || ""} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Formation / Entretien / Réparation..." />
            </div>
            <div>
              <Label>Priorité</Label>
              <Select value={form.priority || "medium"} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="backlog-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Basse</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Date cible (optionnelle)</Label>
            <Input type="date" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="backlog-duedate" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label>Assigner à un salarié</Label>
              <Select value={form.assignee_employee_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_employee_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.first_name} {e.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>ou à un sous-traitant</Label>
              <Select value={form.assignee_subcontractor_id || "none"} onValueChange={(v) => setForm({ ...form, assignee_subcontractor_id: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun</SelectItem>
                  {subcontractors.map(s => <SelectItem key={s.id} value={s.id}>{s.company_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="save-backlog-btn">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- Backlog Tab ----

const BacklogTab = ({ employees, subcontractors }) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCompleted, setShowCompleted] = useState(false);
  const [dialog, setDialog] = useState({ open: false, task: null });

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.getBacklogTasks({ include_completed: showCompleted });
      setTasks(data);
    } catch (_e) {
      toast.error("Erreur chargement");
    } finally {
      setLoading(false);
    }
  }, [showCompleted]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggle = async (t) => {
    try {
      await api.updateBacklogTask(t.id, { completed: !t.completed });
      fetchAll();
    } catch (_e) { toast.error("Erreur"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette tâche ?")) return;
    try {
      await api.deleteBacklogTask(id);
      toast.success("Supprimée");
      fetchAll();
    } catch (_e) { toast.error("Erreur"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin" /></div>;

  const byPriority = { high: [], medium: [], low: [] };
  tasks.forEach(t => { byPriority[t.priority || "medium"].push(t); });

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center flex-wrap gap-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className="bg-red-100 text-red-800 text-base px-3 py-1" data-testid="high-count">{byPriority.high.length} haute(s)</Badge>
          <Badge className="bg-amber-100 text-amber-900 text-base px-3 py-1">{byPriority.medium.length} moyenne(s)</Badge>
          <Badge className="bg-slate-100 text-slate-800 text-base px-3 py-1">{byPriority.low.length} basse(s)</Badge>
          <div className="flex items-center gap-2 ml-3">
            <Checkbox id="show-done" checked={showCompleted} onCheckedChange={(v) => setShowCompleted(!!v)} data-testid="show-completed" />
            <Label htmlFor="show-done" className="cursor-pointer text-sm">Afficher les terminées</Label>
          </div>
        </div>
        <Button onClick={() => setDialog({ open: true, task: null })} data-testid="add-backlog-btn">
          <Plus className="h-4 w-4 mr-1" /> Nouvelle tâche
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {tasks.length === 0 ? (
            <p className="text-center text-muted-foreground py-10">Aucune tâche dans le backlog. Ajoutez-en quand vous pensez à quelque chose à faire plus tard.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12"></TableHead>
                  <TableHead>Tâche</TableHead>
                  <TableHead>Catégorie</TableHead>
                  <TableHead>Priorité</TableHead>
                  <TableHead>Date cible</TableHead>
                  <TableHead>Assigné à</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map(t => {
                  const isOverdue = t.due_date && !t.completed && new Date(t.due_date) < new Date(new Date().toDateString());
                  return (
                  <TableRow
                    key={t.id}
                    className={t.completed ? "opacity-60" : isOverdue ? "bg-red-50/60" : ""}
                    data-testid={`backlog-row-${t.id}`}
                  >
                    <TableCell>
                      <Checkbox checked={!!t.completed} onCheckedChange={() => handleToggle(t)} data-testid={`toggle-backlog-${t.id}`} />
                    </TableCell>
                    <TableCell>
                      <div className={`font-medium ${t.completed ? "line-through" : ""}`}>
                        {t.title}
                        {isOverdue && <Badge className="bg-red-100 text-red-800 text-xs ml-2"><AlertTriangle className="h-3 w-3 mr-1" />En retard</Badge>}
                      </div>
                      {t.description && <div className="text-xs text-muted-foreground">{t.description}</div>}
                    </TableCell>
                    <TableCell><span className="text-sm">{t.category || "—"}</span></TableCell>
                    <TableCell>
                      <Badge className={`${PRIORITY_CONFIG[t.priority || "medium"].className} text-xs`}>
                        <Flag className="h-3 w-3 mr-1" />
                        {PRIORITY_CONFIG[t.priority || "medium"].label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">
                      {t.due_date
                        ? <span className={isOverdue ? "text-red-700 font-semibold" : ""}>{fmtDate(t.due_date)}</span>
                        : <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell className="text-xs">
                      {t.assignee_employee_name && <Badge variant="outline" className="text-xs"><User className="h-3 w-3 mr-1" />{t.assignee_employee_name}</Badge>}
                      {t.assignee_subcontractor_name && <Badge variant="outline" className="text-xs"><Briefcase className="h-3 w-3 mr-1" />{t.assignee_subcontractor_name}</Badge>}
                      {!t.assignee_employee_name && !t.assignee_subcontractor_name && <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setDialog({ open: true, task: t })} data-testid={`edit-backlog-${t.id}`}><Edit className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(t.id)} data-testid={`delete-backlog-${t.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <BacklogTaskDialog
        open={dialog.open}
        task={dialog.task}
        employees={employees}
        subcontractors={subcontractors}
        onClose={() => setDialog({ open: false, task: null })}
        onSaved={() => { setDialog({ open: false, task: null }); fetchAll(); }}
      />
    </div>
  );
};

// ---- Main Page ----

export const AdminTasksPage = () => {
  const [employees, setEmployees] = useState([]);
  const [subcontractors, setSubcontractors] = useState([]);
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") === "contracts" ? "contracts"
    : searchParams.get("tab") === "backlog" ? "backlog"
    : "recurring";

  useEffect(() => {
    Promise.all([api.getEmployees(), api.getSubcontractors()])
      .then(([eRes, sRes]) => {
        setEmployees((eRes.data || []).filter(e => !e.exit_date && !e.archived));
        setSubcontractors(sRes.data);
      })
      .catch(() => { /* silent */ });
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-tasks-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
          <ClipboardList className="h-7 w-7 text-primary" />
          Administratif
        </h1>
        <p className="text-muted-foreground">Tâches récurrentes (échéances fiscales, sociales...) et backlog des choses à faire quand vous avez le temps</p>
      </div>

      <Tabs defaultValue={initialTab}>
        <TabsList>
          <TabsTrigger value="recurring" data-testid="admin-tab-recurring"><Repeat className="h-4 w-4 mr-1" /> Tâches récurrentes</TabsTrigger>
          <TabsTrigger value="backlog" data-testid="admin-tab-backlog"><Inbox className="h-4 w-4 mr-1" /> À faire quand on a le temps</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="admin-tab-contracts"><FileText className="h-4 w-4 mr-1" /> Contrats et charges fixes</TabsTrigger>
          <TabsTrigger value="overrun" data-testid="admin-tab-overrun"><Bell className="h-4 w-4 mr-1" /> Alertes horaires</TabsTrigger>
          <TabsTrigger value="disputes" data-testid="admin-tab-disputes"><AlertTriangle className="h-4 w-4 mr-1" /> Litiges</TabsTrigger>
          <TabsTrigger value="ai-activity" data-testid="admin-tab-ai-activity"><Sparkles className="h-4 w-4 mr-1" /> Activité IA</TabsTrigger>
        </TabsList>
        <TabsContent value="recurring">
          <RecurringTab employees={employees} subcontractors={subcontractors} />
        </TabsContent>
        <TabsContent value="backlog">
          <BacklogTab employees={employees} subcontractors={subcontractors} />
        </TabsContent>
        <TabsContent value="contracts">
          <ContractsTab />
        </TabsContent>
        <TabsContent value="overrun">
          <OverrunSettingsTab />
        </TabsContent>
        <TabsContent value="disputes">
          <DisputesTab />
        </TabsContent>
        <TabsContent value="ai-activity">
          <AIActivityTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminTasksPage;
